import Cookies from 'js-cookie';
import axios from 'axios';
import DomainUtils from './domainUtils';
import logger from './logger';

/**
 * TokenManager - A centralized service for managing authentication tokens
 * across different environments and domains
 */
class TokenManager {
  /**
   * Set the authentication token with appropriate options for the current environment
   * @param {string} token - The authentication token to store
   * @param {Object} options - Additional options (expirationDays, tokenName)
   * @returns {void}
   */
  setToken(token, options = {}) {
    if (!token) return;
    
    const {
      expirationDays = 30,
      tokenName = 'userToken'
    } = options;
    
    // Create expiration date
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + expirationDays);
    
    // Determine cookie options based on environment
    const cookieOptions = { 
      path: '/',
      expires: expirationDate
    };
    
    // Set different cookie options for production (with proper domain)
    const domainConfig = DomainUtils.getDomainConfig();
    if (!domainConfig.isLocalhost) {
      cookieOptions.domain = domainConfig.baseDomain;
      cookieOptions.secure = true;
      cookieOptions.sameSite = 'None';
    }
    
    // Set cookie with the determined options
    Cookies.set(tokenName, token, cookieOptions);

    // Set axios default authorization header for future requests
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // Return full cookie configuration for debugging
    return {
      token: token.substring(0, 10) + '...',
      cookieOptions
    };
  }
  
  /**
   * 🔒 SECURITY FIX: Get token with cookie propagation retry mechanism
   * @param {string} tokenName - The name of the token to retrieve (default: 'userToken')
   * @param {boolean} allowRefresh - Whether to try refreshing the token if not found
   * @returns {Promise<string|null>} The authentication token or null if not found
   */
  async getTokenAsync(tokenName = 'userToken', allowRefresh = true) {
    // Check for redirect loop prevention - don't refresh tokens if in loop
    const redirectCount = parseInt(sessionStorage.getItem('redirectCount') || '0');
    const lastRedirectTime = parseInt(sessionStorage.getItem('lastRedirectTime') || '0');
    const now = Date.now();
    
    if (redirectCount >= 3 && (now - lastRedirectTime) < 10000) {
      allowRefresh = false; // Disable refresh to prevent loops
    }
    
    // 🔒 SECURITY: Check for token consistency first
    if (allowRefresh) {
      this.fixTokenInconsistencies();
    }
    
    // 1. Check sessionStorage first for immediate cross-domain transfers
    const pendingToken = sessionStorage.getItem('pendingAuthToken');
    if (pendingToken) {
      this.setToken(pendingToken, { tokenName });
      // Don't remove yet - let calling code decide
      return pendingToken;
    }
    
    // 2. Try cookie reading with retry mechanism for cross-domain cookie propagation
    let token = await this.getCookieWithRetry(tokenName, 3, 500); // 3 retries, 500ms base delay
    
    return token;
  }

  /**
   * Synchronous version of getToken for backward compatibility
   * @param {string} tokenName - The name of the token to retrieve (default: 'userToken')
   * @param {boolean} allowRefresh - Whether to try refreshing the token if not found
   * @returns {string|null} The authentication token or null if not found
   */
  getToken(tokenName = 'userToken', allowRefresh = true) {
    // Check for redirect loop prevention - don't refresh tokens if in loop
    const redirectCount = parseInt(sessionStorage.getItem('redirectCount') || '0');
    const lastRedirectTime = parseInt(sessionStorage.getItem('lastRedirectTime') || '0');
    const now = Date.now();

    if (redirectCount >= 3 && (now - lastRedirectTime) < 10000) {
      allowRefresh = false; // Disable refresh to prevent loops
    }

    // 🔒 SECURITY: Check for token consistency first
    if (allowRefresh) {
      this.fixTokenInconsistencies();
    }

    // 1. Check sessionStorage first for immediate cross-domain transfers
    const pendingToken = sessionStorage.getItem('pendingAuthToken');
    if (pendingToken) {
      this.setToken(pendingToken, { tokenName });
      return pendingToken;
    }

    // 2. Try to get from cookies (synchronous)
    const token = Cookies.get(tokenName);

    return token;
  }

  /**
   * 🔒 TIMING FIX: Cookie reading with retry mechanism for cross-domain propagation
   * @param {string} tokenName - Cookie name to read
   * @param {number} maxRetries - Maximum number of retry attempts
   * @param {number} baseDelay - Base delay in milliseconds (will use exponential backoff)
   * @returns {Promise<string|null>} The cookie value or null
   */
  async getCookieWithRetry(tokenName, maxRetries = 3, baseDelay = 500) {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      const token = Cookies.get(tokenName);

      if (token) {
        return token;
      }

      // If this is the last attempt, don't wait
      if (attempt === maxRetries) {
        break;
      }

      // Exponential backoff: 500ms, 1000ms, 2000ms
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    return null;
  }
  
  /**
   * Remove the authentication token from all storages
   * @param {string} tokenName - The name of the token to remove (default: 'userToken')
   * @returns {void}
   */
  removeToken(tokenName = 'userToken') {
    // Prepare cookie options for removal
    const cookieOptions = {
      path: '/'
    };
    
    // Set domain for production
    const domainConfig = DomainUtils.getDomainConfig();
    if (!domainConfig.isLocalhost) {
      cookieOptions.domain = domainConfig.baseDomain;
    }
    
    // Remove from cookies
    Cookies.remove(tokenName, cookieOptions);
    
    // For extra thoroughness, try specific subdomains as fallback
    const domainConfig2 = DomainUtils.getDomainConfig();
    if (!domainConfig2.isLocalhost) {
      domainConfig2.subdomains.forEach(subdomain => {
        Cookies.remove(tokenName, { 
          domain: `${subdomain}${domainConfig2.baseDomain}`, 
          path: '/' 
        });
      });
    }
    
    // Note: localStorage token storage removed for security
    
    // Clear authorization header
    delete axios.defaults.headers.common['Authorization'];
  }
  
  /**
   * Check if there is a valid auth token
   * @param {string} tokenName - The name of the token to check (default: 'userToken')
   * @returns {boolean} True if a token exists
   */
  isAuthenticated(tokenName = 'userToken') {
    return !!this.getToken(tokenName);
  }

  /**
   * 🔒 VERIFICATION FIX: Verify cookie availability before making authentication decisions
   * @param {string} tokenName - The token name to verify
   * @returns {Promise<{available: boolean, token: string|null, source: string}>}
   */
  async verifyCookieAvailability(tokenName = 'userToken') {
    const result = {
      available: false,
      token: null,
      source: 'none'
    };

    // 1. Check if we have a pending token (highest priority)
    const pendingToken = sessionStorage.getItem('pendingAuthToken');
    if (pendingToken) {
      result.available = true;
      result.token = pendingToken;
      result.source = 'sessionStorage_pending';
      return result;
    }

    // 2. Try immediate cookie read
    let token = Cookies.get(tokenName);
    if (token) {
      result.available = true;
      result.token = token;
      result.source = 'cookies_immediate';
      return result;
    }

    // 3. Try with retry mechanism for cross-domain cookie propagation
    token = await this.getCookieWithRetry(tokenName, 2, 750); // 2 retries, 750ms base delay

    if (token) {
      result.available = true;
      result.token = token;
      result.source = 'cookies_retry';
      return result;
    }

    return result;
  }

  /**
   * 🔒 VERIFICATION FIX: Enhanced authentication check with cookie verification
   * @param {string} tokenName - The token name to check
   * @returns {Promise<boolean>} True if authenticated and cookie is available
   */
  async isAuthenticatedWithVerification(tokenName = 'userToken') {
    const verification = await this.verifyCookieAvailability(tokenName);

    if (verification.available) {
      // If token was found via retry or sessionStorage, ensure it's set in cookies
      if (verification.source !== 'cookies_immediate' && verification.token) {
        this.setToken(verification.token, { tokenName });
      }

      return true;
    }

    return false;
  }
  
  /**
   * Handle logout process by clearing all auth tokens and related data
   * @param {Object} options - Logout options (redirect, redirectDelay)
   * @returns {Promise<void>}
   */
  async logout(options = {}) {
    const {
      redirect = true,
      redirectDelay = 500,
      redirectUrl = null
    } = options;

    // Clear ALL possible token names and variations
    const tokenNames = ['userToken', 'tempMfaToken', 'accountInfo', 'currentAccount', 'accounts'];
    
    // Remove tokens from cookies with multiple domain strategies
    tokenNames.forEach(tokenName => {
      // Strategy 1: Remove from current domain
      Cookies.remove(tokenName, { path: '/' });
      
      // Strategy 2: Remove from base domain (production)
      const domainConfigLogout = DomainUtils.getDomainConfig();
      if (!domainConfigLogout.isLocalhost) {
        Cookies.remove(tokenName, { 
          domain: domainConfigLogout.baseDomain, 
          path: '/',
          secure: true,
          sameSite: 'None'
        });
        
        // Strategy 3: Remove from specific subdomains as fallback
        domainConfigLogout.subdomains.forEach(subdomain => {
          Cookies.remove(tokenName, { 
            domain: `${subdomain}${domainConfigLogout.baseDomain}`, 
            path: '/',
            secure: true,
            sameSite: 'None'
          });
          
          // Also try without explicit domain
          Cookies.remove(tokenName, { 
            path: '/',
            secure: true,
            sameSite: 'None'
          });
        });
      }
    });
    
    // Clear ALL localStorage items that could contain sensitive data
    const localStorageKeysToRemove = [
      'userToken',
      'tempMfaToken', 
      'currentAccount',
      'accounts',
      'accountInfo',
      'isAdmin',
      'userType',
      'firstName',
      'lastName',
      'isBusinessOwner',
      'businessInfo',
      'lastPath',
      'darkMode', // Preserve user preference
      'pendingBusinessSetup',
      'businessSetupData',
      'businessRegistrationData'
    ];
    
    // Preserve darkMode but clear everything else
    // const darkMode = localStorage.getItem('darkMode'); // Unused variable
    localStorageKeysToRemove.forEach(key => {
      if (key !== 'darkMode') {
        localStorage.removeItem(key);
      }
    });
    
    // Clear ALL sessionStorage items
    const sessionStorageKeysToRemove = [
      'pendingAuthToken',
      'pendingUserData',
      'previousUserToken',
      'previousAccount',
      'redirectCount',
      'businessRegistrationData',
      'addingNewAccount',
      'showSecurityPrompt',
      'pendingBusinessSetup'
    ];
    
    sessionStorageKeysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    // Clear axios authorization header
    if (typeof axios !== 'undefined') {
      delete axios.defaults.headers.common['Authorization'];
    }
    
    // Clear axiosInstance authorization header if available
    if (window.axiosInstance) {
      delete window.axiosInstance.defaults.headers.common['Authorization'];
    }
    
    // Strategy 4: Use server-side cookie clearing for extra security
    try {
      // Try multiple endpoints for comprehensive clearing
      const clearEndpoints = [
        '/clear-all-cookies',
        '/api/auth/logout',
        '/signout'
      ];

      // Make requests to clear cookies server-side
      const clearPromises = clearEndpoints.map(endpoint =>
        fetch(endpoint, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }).catch(() => {})
      );

      await Promise.allSettled(clearPromises);
    } catch (e) {
      // Silent fail - not critical
    }
    
    // Strategy 5: Force reload to clear any remaining state
    if (redirect) {
      const domainConfigRedirectUrl = DomainUtils.getDomainConfig();
      const logoutUrl = redirectUrl || (domainConfigRedirectUrl.isLocalhost
        ? '/login'
        : DomainUtils.buildUrl('accounts', '/signout'));

      // Add delay to ensure all operations complete
      setTimeout(() => {
        // Use location.replace to prevent back button issues
        window.location.replace(logoutUrl);
      }, redirectDelay);
    } else {
      // If not redirecting, at least reload to clear state
      setTimeout(() => {
        window.location.reload();
      }, redirectDelay);
    }
  }
  
  /**
   * Get current account info from cookies
   * @returns {Object|null} The current account data or null if not found
   */
  getCurrentAccount() {
    try {
      const accountCookie = Cookies.get('currentAccount');
      if (accountCookie) {
        return JSON.parse(accountCookie);
      }
      
      // Fallback: check localStorage for migration purposes
      const localStorageAccount = localStorage.getItem('currentAccount');
      if (localStorageAccount) {
        const accountData = JSON.parse(localStorageAccount);
        // Migrate to cookie and remove from localStorage
        this.setAccountInfo(accountData);
        localStorage.removeItem('currentAccount');
        return accountData;
      }
      
      return null;
    } catch (error) {
      logger.error('Error getting current account:', error);
      return null;
    }
  }
  
  /**
   * Store account info across domains
   * @param {Object} accountData - The account data to store
   * @returns {void}
   */
  setAccountInfo(accountData) {
    if (!accountData) return;
    
    // Store current account in secure cookie instead of localStorage
    const cookieOptions = {
      path: '/',
      expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
    };
    
    const domainConfigAccount = DomainUtils.getDomainConfig();
    if (!domainConfigAccount.isLocalhost) {
      cookieOptions.domain = domainConfigAccount.baseDomain;
      cookieOptions.secure = true;
      cookieOptions.sameSite = 'None';
    }
    
    // Store account data in cookie including token for cross-subdomain account switching
    const accountForCookie = {
      userId: accountData.userId,
      email: accountData.email,
      firstName: accountData.firstName || '',
      lastName: accountData.lastName || '',
      profilePicture: accountData.profilePicture || null,
      token: accountData.token || null,
      loginTimestamp: accountData.loginTimestamp || Date.now(),
      lastAccessed: accountData.lastAccessed || Date.now()
    };

    Cookies.set('currentAccount', JSON.stringify(accountForCookie), cookieOptions);

    // Update the full accounts list in localStorage
    try {
      const existingAccounts = JSON.parse(localStorage.getItem('accounts') || '[]');

      // Remove this account if it already exists (to avoid duplicates)
      const filteredAccounts = existingAccounts.filter(acc =>
        acc.userId !== accountData.userId && acc.email !== accountData.email
      );

      // Add updated account data to the end (most recently used)
      filteredAccounts.push({
        ...accountData,
        lastAccessed: Date.now()
      });

      // Save updated accounts list
      localStorage.setItem('accounts', JSON.stringify(filteredAccounts));

      // Store in cookie with appropriate domain settings for cross-subdomain sharing
      const cookieOptionsInner = {
        path: '/',
        expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
      };

      const domainConfigInner = DomainUtils.getDomainConfig();
      if (!domainConfigInner.isLocalhost) {
        cookieOptionsInner.domain = domainConfigInner.baseDomain;
        cookieOptionsInner.secure = true;
        cookieOptionsInner.sameSite = 'None';
      }

      // Sync the full accounts list (with tokens) to the 'accounts' cookie
      try {
        Cookies.set('accounts', JSON.stringify(filteredAccounts), cookieOptionsInner);
      } catch (e) {
        logger.warn('Could not set accounts cookie (size limit?):', e);
      }

      // Get all accounts for sharing via cookie
      const allAccounts = filteredAccounts;

      // Create minimal version for accountInfo cookie (without sensitive data)
      const accountsForCookie = allAccounts.map(acc => ({
        userId: acc.userId,
        email: acc.email,
        firstName: acc.firstName || '',
        lastName: acc.lastName || '',
        profilePicture: acc.profilePicture || null
      }));

      const accountInfoCookie = {
        accounts: accountsForCookie,
        currentUserId: accountData.userId,
        timestamp: Date.now()
      };

      // Set the accountInfo cookie with the full accounts list
      Cookies.set('accountInfo', JSON.stringify(accountInfoCookie), cookieOptionsInner);

    } catch (error) {
      logger.error('Error updating accounts list:', error);
    }
  }
  
  /**
   * 🔒 TIMING FIX: Enhanced cross-domain redirect with comprehensive token preservation
   * @param {string} destination - URL to redirect to
   * @param {Object} userData - User data to preserve through the redirect
   * @returns {void}
   */
  redirectWithAuthentication(destination, userData) {
    if (!destination) return;

    // Store token in sessionStorage for immediate access on target domain
    const token = this.getToken();
    if (token) {
      sessionStorage.setItem('pendingAuthToken', token);
    }

    // Store user data if provided
    if (userData) {
      sessionStorage.setItem('pendingUserData', JSON.stringify(userData));
    }

    // Create account data for immediate restoration
    if (token && userData && userData.email) {
      const accountData = {
        userId: userData.id,
        email: userData.email,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        token: token,
        loginTimestamp: Date.now()
      };

      // Set account info in cookies for cross-domain access
      this.setAccountInfo(accountData);
    }

    // Reset any existing redirect counters
    sessionStorage.removeItem('redirectCount');
    sessionStorage.removeItem('lastRedirectTime');

    // For cross-domain redirects in production, add a delay to allow cookie propagation
    const domainConfigCrossDomain = DomainUtils.getDomainConfig();
    if (!domainConfigCrossDomain.isLocalhost) {
      setTimeout(() => {
        window.location.href = destination;
      }, 1500); // Increased delay even more
    } else {
      window.location.href = destination;
    }
  }
  
  /**
   * Manage account switching and ensure proper token and account data handling
   * @param {Object} account - The account to switch to
   * @param {Object} options - Optional configuration
   * @returns {Promise<boolean>} Success status
   */
  async switchAccount(account, options = {}) {
    if (!account || !account.token) {
      logger.error('Invalid account data for switching');
      return false;
    }
    
    try {
      const {
        updateUI = () => {},
        onSuccess = () => {},
        // onError = () => {}, // Unused variable
        reloadPage = true
      } = options;

      // First, clear any existing tokens to prevent conflicts
      this.removeToken('userToken');
      this.removeToken('tempMfaToken');
      this.removeToken('accountInfo');
      
      // Set the token for the new account with proper cookie options
      const domainConfigSwitch = DomainUtils.getDomainConfig();
      const cookieOptions = {
        path: '/',
        expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
        secure: !domainConfigSwitch.isLocalhost,
        sameSite: domainConfigSwitch.isLocalhost ? undefined : 'None'
      };
      
      if (!domainConfigSwitch.isLocalhost) {
        cookieOptions.domain = domainConfigSwitch.baseDomain;
      }
      
      // Set cookie with explicit options
      Cookies.set('userToken', account.token, cookieOptions);
      
      // Note: localStorage token storage removed for security
      
      // Update current account info to ensure cookies are properly set
      this.setAccountInfo(account);
      
      // Allow UI updates to show the change immediately
      updateUI(account);
      
      // Force state refresh by making a test API call with the new token
      try {
        // Refresh token state in axios
        axios.defaults.headers.common['Authorization'] = `Bearer ${account.token}`;
        
        // Make a test API call to check token validity and refresh state
        const response = await axios.get('/api/user', {
          headers: {
            'Authorization': `Bearer ${account.token}`,
            'Cache-Control': 'no-cache'
          }
        });
        
      } catch (apiError) {
        // API validation failed, continue anyway
      }
      
      // Give cookies time to propagate (important for cross-domain functionality)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Call success callback
      onSuccess(account);
      
      // For cross-domain support, set key cookie values in sessionStorage as well
      sessionStorage.setItem('pendingAuthToken', account.token);
      sessionStorage.setItem('pendingUserData', JSON.stringify({
        id: account.userId,
        email: account.email,
        firstName: account.firstName || '',
        lastName: account.lastName || ''
      }));
      
      // Reload the page if requested
      if (reloadPage) {
        // Get current URL information
        const currentUrl = new URL(window.location.href);
        const currentPath = currentUrl.pathname;
        const currentSubdomain = currentUrl.hostname.split('.')[0];
        
        // Determine if we're in production or localhost
        const domainConfigRedirect = DomainUtils.getDomainConfig();
        if (!domainConfigRedirect.isLocalhost) {
          // In production, maintain the current subdomain and path
          const baseUrl = window.location.protocol + '//' + window.location.host;
          
          // If we're on the root path or a path that might not exist in the new account context,
          // redirect to a safe default path for that subdomain
          let targetPath = currentPath;
          
          // Define safe default paths for each subdomain
          const defaultPaths = {
            'mail': '/inbox',
            'accounts': '/profile',
            'admin': '/home/dashboard',
            'drive': '/',
            'meetings': '/'
          };
          
          // If we're on a root path or the current path might not be valid for the new account,
          // use the default path for that subdomain
          if (currentPath === '/' || currentPath === '') {
            targetPath = defaultPaths[currentSubdomain] || '/';
          }
          
          // Store the target URL in sessionStorage before navigation
          const finalUrl = `${baseUrl}${targetPath}`;
          sessionStorage.setItem('pendingRedirect', finalUrl);
          
          // Use replace to avoid adding to browser history
          window.location.replace(finalUrl);
          
          // Fallback reload after 2 seconds if we're still on the same page
          setTimeout(() => {
            if (window.location.href === currentUrl.href) {
              window.location.reload(true);
            }
          }, 2000);
        } else {
          // For localhost, maintain the current path
          window.location.replace(currentPath || '/');
        }
      }
      
      return true;
    } catch (error) {
      logger.error('Error switching accounts:', error);
      if (options.onError) {
        options.onError(error);
      }
      return false;
    }
  }
  
  /**
   * Validate token consistency across all storage methods
   * @returns {Object} Validation results and inconsistencies found
   */
  validateTokenConsistency() {
    const results = {
      consistent: true,
      tokens: {},
      inconsistencies: []
    };
    
    // Check all possible token storage locations
    const tokenNames = ['userToken', 'tempMfaToken', 'accountInfo'];
    
    tokenNames.forEach(tokenName => {
      const cookieToken = Cookies.get(tokenName);
      const sessionStorageToken = sessionStorage.getItem(tokenName);
      
      results.tokens[tokenName] = {
        cookie: cookieToken ? 'EXISTS' : 'MISSING',
        sessionStorage: sessionStorageToken ? 'EXISTS' : 'MISSING'
      };
      
      // Check for inconsistencies
      if (sessionStorageToken && sessionStorageToken !== cookieToken) {
        results.consistent = false;
        results.inconsistencies.push(`${tokenName}: SessionStorage differs from cookie`);
      }
    });
    
    return results;
  }

  /**
   * Fix token inconsistencies by using the most recent/valid token
   * @returns {boolean} Whether inconsistencies were found and fixed
   */
  fixTokenInconsistencies() {
    const validation = this.validateTokenConsistency();
    
    if (validation.consistent) {
      return false;
    }

    // Clear any sessionStorage tokens that don't match cookies
    const tokenNames = ['userToken', 'tempMfaToken'];

    tokenNames.forEach(tokenName => {
      const cookieToken = Cookies.get(tokenName);
      const sessionToken = sessionStorage.getItem(tokenName);

      if (sessionToken && sessionToken !== cookieToken) {
        sessionStorage.removeItem(tokenName);
      }
    });

    return true;
  }

  /**
   * Clean up legacy localStorage tokens for security
   * This should be called on app initialization
   */
  cleanupLegacyStorage() {
    const legacyTokens = ['userToken', 'tempMfaToken'];
    let cleaned = false;

    legacyTokens.forEach(tokenName => {
      const cookieToken = Cookies.get(tokenName);
      const localStorageToken = localStorage.getItem(tokenName);

      if (localStorageToken) {
        // If cookie doesn't exist but localStorage does, restore to cookie
        if (!cookieToken) {
          this.setToken(localStorageToken, { tokenName });
        }

        // Always remove from localStorage for security
        localStorage.removeItem(tokenName);
        cleaned = true;
      }
    });
  }

  /**
   * Remove a specific account from cookies and localStorage
   * @param {Object} accountToRemove - The account to remove
   * @returns {void}
   */
  removeAccountFromCookies(accountToRemove) {
    if (!accountToRemove || !accountToRemove.email) {
      logger.error('Invalid account data for removal');
      return;
    }

    try {
      // Get current account to check if we're removing the active account
      const currentAccount = this.getCurrentAccount();
      const isCurrentAccount = currentAccount && currentAccount.email === accountToRemove.email;

      // Remove from localStorage accounts list
      const existingAccounts = JSON.parse(localStorage.getItem('accounts') || '[]');
      const filteredAccounts = existingAccounts.filter(acc => 
        acc.email !== accountToRemove.email && acc.userId !== accountToRemove.userId
      );
      localStorage.setItem('accounts', JSON.stringify(filteredAccounts));

      // If we're removing the current account, clear main auth cookies
      if (isCurrentAccount) {
        this.removeToken('userToken');
        this.removeToken('currentAccount');
        this.removeToken('accountInfo');
      }

      // Update the accountInfo cookie with the new accounts list
      if (filteredAccounts.length > 0) {
        const accountsForCookie = filteredAccounts.map(acc => ({
          userId: acc.userId,
          email: acc.email,
          firstName: acc.firstName || '',
          lastName: acc.lastName || '',
          profilePicture: acc.profilePicture || null
        }));

        const accountInfoCookie = {
          accounts: accountsForCookie,
          currentUserId: isCurrentAccount ? null : currentAccount?.userId,
          timestamp: Date.now()
        };

        const cookieOptions = {
          path: '/',
          expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
        };

        const domainConfig = DomainUtils.getDomainConfig();
        if (!domainConfig.isLocalhost) {
          cookieOptions.domain = domainConfig.baseDomain;
          cookieOptions.secure = true;
          cookieOptions.sameSite = 'None';
        }

        Cookies.set('accountInfo', JSON.stringify(accountInfoCookie), cookieOptions);
      } else {
        // No accounts left, clear accountInfo cookie
        const domainConfig = DomainUtils.getDomainConfig();
        const cookieOptions = { path: '/' };
        if (!domainConfig.isLocalhost) {
          cookieOptions.domain = domainConfig.baseDomain;
        }
        Cookies.remove('accountInfo', cookieOptions);
      }
    } catch (error) {
      logger.error('Error removing account from cookies:', error);
    }
  }

  /**
   * 🔒 TIMING FIX: Enhanced cross-domain authentication synchronization with immediate restoration
   * Handles authentication state across subdomains with loop prevention and timing fixes
   * @returns {Promise<boolean>} Whether authentication was successfully synchronized
   */
  async synchronizeAuthenticationAcrossDomains() {
    // Dedup concurrent callers: multiple components/effects mounting on the same
    // subdomain navigation used to race and clobber each other's token writes.
    // Everyone awaits the same in-flight promise instead.
    if (this._syncInFlight) return this._syncInFlight;
    this._syncInFlight = this._doSynchronize();
    try {
      return await this._syncInFlight;
    } finally {
      this._syncInFlight = null;
    }
  }

  async _doSynchronize() {
    try {
      // Check for redirect loop prevention
      const redirectCount = parseInt(sessionStorage.getItem('redirectCount') || '0');
      const lastRedirectTime = parseInt(sessionStorage.getItem('lastRedirectTime') || '0');
      const now = Date.now();

      if (redirectCount >= 3 && (now - lastRedirectTime) < 30000) {
        return this.isAuthenticated();
      }

      // 1. PRIORITY: Check for pending authentication from cross-domain navigation
      const pendingToken = sessionStorage.getItem('pendingAuthToken');
      const pendingUserData = sessionStorage.getItem('pendingUserData');

      if (pendingToken) {
        // Set the token in cookies immediately
        this.setToken(pendingToken);

        // If we have user data, create account record
        if (pendingUserData) {
          try {
            const userData = JSON.parse(pendingUserData);
            if (userData && userData.email) {
              const accountData = {
                userId: userData.id,
                email: userData.email,
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                token: pendingToken,
                loginTimestamp: Date.now()
              };

              this.setAccountInfo(accountData);
            }
          } catch (e) {
            logger.error('Error processing pending user data:', e);
          }
        }

        // Clean up pending tokens after successful sync
        sessionStorage.removeItem('pendingAuthToken');
        sessionStorage.removeItem('pendingUserData');

        // Reset redirect count on successful sync
        sessionStorage.removeItem('redirectCount');
        sessionStorage.removeItem('lastRedirectTime');
        sessionStorage.removeItem('accountsRedirectTime');

        return true;
      }

      // 2. Check if we're in an auth grace period and should retry cookie reading
      const accountsRedirectTime = sessionStorage.getItem('accountsRedirectTime');
      if (accountsRedirectTime) {
        const timeSinceRedirect = now - parseInt(accountsRedirectTime);
        if (timeSinceRedirect < 5000) { // Within 5 second grace period
          // Try async token retrieval with retry mechanism
          const token = await this.getTokenAsync();
          if (token) {
            sessionStorage.removeItem('accountsRedirectTime');
            sessionStorage.removeItem('redirectCount');
            sessionStorage.removeItem('lastRedirectTime');
            return true;
          }
        }
      }
      
      // 3. Enhanced token verification with cookie availability check
      const hasToken = await this.isAuthenticatedWithVerification();
      
      // 4. If authenticated, reset any redirect counters
      if (hasToken) {
        sessionStorage.removeItem('redirectCount');
        sessionStorage.removeItem('lastRedirectTime');
        sessionStorage.removeItem('accountsRedirectTime');
      }
      
      return hasToken;
    } catch (error) {
      logger.error('❌ Error synchronizing authentication across domains:', error);
      return false;
    }
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

// Clean up legacy storage on initialization
tokenManager.cleanupLegacyStorage();

export default tokenManager; 